import { useMemo } from 'react';
import { get, useWatch } from 'react-hook-form';

import {
  Accordion,
  AccordionProps,
  AccordionVariant,
} from '@/components/Accordion/Accordion';
import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { useFormContext } from '@/components/react-hook-form';
import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import { useSuggestionAcknowledgementContext } from '@/modules/aiSuggestions/context/SuggestionAcknowledgement.context';
import { BENEFICIARY_LEVEL_CURRENT_ACCESS_DISPLAY_NAMES } from '@/modules/beneficiaries/beneficiaries.constants';
import { useIsCurrentBeneficiaryEntity } from '@/modules/beneficiaries/beneficiaries.hooks';
import { EntityKind } from '@/types/schema';

import { useEntityDetailsContext } from '../contexts/entityDetails/entityDetails.context';
import {
  EntityBeneficiariesFormBeneficiary,
  EntityBeneficiariesFormBodyProps,
  EntityBeneficiariesFormNamespace,
  EntityBeneficiariesFormSecondNamespace,
} from './EntityBeneficiariesSubform.types';
import {
  EntityBeneficiaryFormBodyV2,
  EntityBeneficiaryFormBodyV2Props,
  getBeneficiaryFormFieldName,
} from './EntityBeneficiaryFormBodyV2';

interface UseAccordionPropsInput {
  fieldName: `${EntityBeneficiariesFormNamespace}.${EntityBeneficiariesFormSecondNamespace}.${number}`;
  beneficiaryOptions: TypeaheadSelectInputOption<string>[];
  beneficiaryIndex: number;
  listItemId: string;
  isDuplicateResolution?: boolean;
}

function useAccordionProps({
  fieldName,
  beneficiaryOptions,
  beneficiaryIndex,
  listItemId,
  isDuplicateResolution,
}: UseAccordionPropsInput): Omit<AccordionProps, 'children'> {
  const { control } = useFormContext<EntityBeneficiariesFormBeneficiary>();
  const isCurrentBeneficiaryEntity = useIsCurrentBeneficiaryEntity();

  const beneficiaryId = useWatch({
    name: getBeneficiaryFormFieldName(fieldName, 'beneficiaryId'),
    control,
  });

  const level = useWatch({
    name: getBeneficiaryFormFieldName(fieldName, 'level'),
    control,
  });

  const title = useMemo(() => {
    const beneficiary = beneficiaryOptions.find(
      (b) => b.value === beneficiaryId
    );

    if (beneficiary && 'display' in beneficiary) {
      return beneficiary.display;
    }

    if (isCurrentBeneficiaryEntity) {
      return EMPTY_CONTENT_HYPHEN;
    }

    return `Beneficiary ${beneficiaryIndex + 1}`;
  }, [
    beneficiaryId,
    beneficiaryOptions,
    beneficiaryIndex,
    isCurrentBeneficiaryEntity,
  ]);

  const { entityKind } = useEntityDetailsContext();
  const isRetirement = entityKind === EntityKind.RetirementPersonalAccount;

  const subtitle = useMemo(() => {
    if (isRetirement) return '';
    if (!level) return '';

    return BENEFICIARY_LEVEL_CURRENT_ACCESS_DISPLAY_NAMES[level];
  }, [isRetirement, level]);

  const variant = useMemo<AccordionVariant>(
    () => (!!isDuplicateResolution ? 'default' : 'filled'),
    [isDuplicateResolution]
  );

  return {
    variant,
    title,
    subtitle,
    isDefaultExpanded: !beneficiaryId,
    hideSubtitleOnOpen: !subtitle,
    id: listItemId,
  };
}

export function EntityBeneficiaryFormBody(
  props: EntityBeneficiariesFormBodyProps & { listItemId: string }
) {
  const {
    control,
    formState: { errors, isSubmitted },
  } = useFormContext<EntityBeneficiariesFormBeneficiary>();
  const fieldName =
    `${props.namespace}.${props.secondNamespace}.${props.i}` as const;

  const { acknowledgedSuggestions, previouslyAcknowledgedSuggestions } =
    useSuggestionAcknowledgementContext();

  const beneficiaryId = useWatch({
    name: getBeneficiaryFormFieldName(fieldName, 'beneficiaryId'),
    control,
  });

  // We want to filter out suggestions that have been acknowledged previously and only
  // consider suggestions that have been acknowledged during this edit session
  const newAcknowledgedSuggestions = useMemo(() => {
    if (!acknowledgedSuggestions || !previouslyAcknowledgedSuggestions)
      return [];

    return Object.values(acknowledgedSuggestions).filter(
      (suggestion) =>
        !Object.values(previouslyAcknowledgedSuggestions).some(
          (prevSuggestion) =>
            prevSuggestion.clientProfileId === suggestion.clientProfileId &&
            prevSuggestion.clientOrganizationId ===
              suggestion.clientOrganizationId
        )
    );
  }, [acknowledgedSuggestions, previouslyAcknowledgedSuggestions]);

  // Checks to see if the beneficiary has been populated from AI suggestions during this edit session
  const isPopulatedFromAI = useMemo(() => {
    if (
      newAcknowledgedSuggestions.some(
        (suggestion) =>
          suggestion.clientProfileId === beneficiaryId ||
          suggestion.clientOrganizationId === beneficiaryId
      )
    ) {
      return true;
    }
    return false;
  }, [beneficiaryId, newAcknowledgedSuggestions]);

  // Check if the beneficiary form has validation errors and force expansion if it does
  const forceExpansion = useMemo(() => {
    if (!beneficiaryId) {
      return true;
    }

    if (isPopulatedFromAI) {
      if (props.additionalItemRendererProps?.duplicateResolutionConfigs) {
        // Don't auto-expand the accordion in the duplicate resolution card
        return false;
      } else {
        return true;
      }
    }

    // Use a simpler approach to check for errors in this specific beneficiary
    // Convert the form path to a string to check if there are any errors
    const formPath = `${props.namespace}.${props.secondNamespace}.${props.i}`;

    // Check if there are any errors in the form state
    let beneficiaryHasFieldError = false;

    // Only check for errors if the form has been submitted
    if (isSubmitted && Object.keys(errors).length > 0) {
      beneficiaryHasFieldError = !!get(errors, formPath);
    }

    // Return undefined if there's no error because we want the default internal handling of the accordion,
    // rather than passing false and forcing it to be closed
    return beneficiaryHasFieldError ? true : undefined;
  }, [
    beneficiaryId,
    errors,
    isPopulatedFromAI,
    isSubmitted,
    props.additionalItemRendererProps?.duplicateResolutionConfigs,
    props.i,
    props.namespace,
    props.secondNamespace,
  ]);

  if (
    props.secondNamespace ===
      EntityBeneficiariesFormSecondNamespace.LeadBeneficiary ||
    props.secondNamespace ===
      EntityBeneficiariesFormSecondNamespace.IncomeBeneficiaries
  ) {
    throw new Error(
      'This component should not be used for charitable beneficiaries'
    );
  }

  const v2Props: EntityBeneficiaryFormBodyV2Props = useMemo(
    () => ({
      beneficiaryOptions: props.beneficiaryOptions,
      householdId: props.householdId,
      variant: props.variant,
      splitScreen: props.splitScreen,
      additionalItemRendererProps: props.additionalItemRendererProps,
      fieldName,
      beneficiaryIndex: props.i,
      remove: props.remove,
      suggestionKindOverride: props.suggestionKindOverride,
    }),
    [
      fieldName,
      props.additionalItemRendererProps,
      props.beneficiaryOptions,
      props.householdId,
      props.i,
      props.remove,
      props.splitScreen,
      props.suggestionKindOverride,
      props.variant,
    ]
  );

  const accordionProps = useAccordionProps({
    fieldName,
    beneficiaryOptions: props.beneficiaryOptions,
    beneficiaryIndex: props.i,
    listItemId: props.listItemId,
    isDuplicateResolution:
      !!props.additionalItemRendererProps?.duplicateResolutionConfigs,
  });

  return (
    <Accordion
      {...accordionProps}
      isDefaultExpanded={forceExpansion} // Expand if populated from AI, empty, or has validation errors
      expanded={isPopulatedFromAI}
    >
      <EntityBeneficiaryFormBodyV2 {...v2Props} />
    </Accordion>
  );
}
